/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { get, isEmpty, isFunction, isObject } from 'lodash';

// Components
import BulkEditor from 'shared/ui/components/modals/BulkEditor.vue';
import ListExportModal from 'shared/ui/components/modals/ListExportModal.vue';
import LoaderButton from 'shared/ui/components/form-components/LoaderButton.vue';
import SearchBar from 'shared/ui/components/form-components/SearchBar.vue';
import SmartTable from 'shared/ui/components/tables/SmartTable.vue';

/**
 * @typedef {Object} UserRow
 * @property {string} _id
 * @property {string} _rev
 * @property {string} fname
 * @property {string} lname
 * @property {string} full_name
 * @property {string|""} company
 * @property {string} fp_status
 * @property {string} registration
 * @property {number} [invitedAt]
 * @property {number} [activatedAt]
 */

const __vue_script__ = {
    name: 'UsersList',

    components: { LoaderButton, SearchBar, SmartTable },

    props: {
        params: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            canSendEmails: this.$services.user.hasPermission('event_actions.send_email', { event_id: this.params.event._id }),
            cols: [],
            currentPage: 1,
            eid: this.params.event._id,
            loading: false,
            metadata: {},
            rows: [],
            searchTerm: '',
            sort: 'ASC',
            sortField: null,
            total: null,
            processing: false,
            someActiveSelected: false,
            someHiddenSelected: false
        };
    },

    watch: {
        searchTerm() {
            this.loadData();
        }
    },

    async created() {
        this.metadata = await this.$services.metadata.getMetadataForType(this.eid, 'person');
        this.cols = this.getColsConfig();
        this.loadData();
    },

    methods: {
        /**
         * Loads the collection to display on the table
         *
         * @param {number} page the page to load
         */
        async loadData(page = this.currentPage) {
            try {
                if (this.loading) {
                    return;
                }

                this.loading = true;

                const { total, items } = await this.$services.pax.getAll(this.eid, {
                    page,
                    reverse: this.sort !== 'ASC',
                    sort: this.sortField,
                    search: this.searchTerm
                });

                /** @type {UserRow[]} */
                this.rows = items;
                this.total = total;
                this.currentPage = page;

            } catch (error) {
                console.error('[UsersList] Could not load data', error.message);
                this.$notify({
                    group: 'flashes',
                    title: this.$i18n.t('general.error_occurred').toString(),
                    text: this.$i18n.t('general.try_later').toString(),
                    type: 'error',
                    duration: 5000
                });
            } finally {
                this.loading = false;
            }
        },

        /**
         * @returns {object[]} each column configuration
         */
        getColsConfig() {
            const { timeFromNow, toLongDateTimeFormat } = this.$utils.time;

            const cols = [
                {
                    field: 'full_name',
                    label: 'profile.details.form.full_name',
                    link: '/event/{{eventId}}/participants/{{_id}}',
                    sortable: 'lname'
                },
                {
                    field: 'company',
                    sortable: 'company',
                    format: item => isEmpty(item.company) ? this.$t('listings.labels.empty') : item.company
                },
                {
                    field: 'registration',
                    cssClass: 'registration-status',
                    label: 'profile.details.computed.registration',
                    format: item => {
                        const regLabel = this.$t(`listings.labels.registration.${item.registration}`);
                        // if the created source is a data api, the label will be the api name,
                        // so there will not be a predefined translation
                        if (regLabel.includes('listings.labels.registration')) {
                            return item.registration;
                        }
                        return regLabel;
                    }
                },
                {
                    field: 'invited_at',
                    label: 'profile.details.computed.invited',
                    format: item => item.invited_at ? timeFromNow(item.invited_at) : '×',
                    tip: (item, $i18n) => item.invited_at ? $i18n.t('profile.details.computed.invited_at', [toLongDateTimeFormat(item.invited_at * 1000)]) : null
                },
                {
                    field: 'activated',
                    label: 'profile.details.computed.activated',
                    format: item => item.activated_at ? timeFromNow(item.activated_at) : '×',
                    tip: (item, $i18n) => item.activated_at ? $i18n.t('profile.details.computed.pax_activated_at', [toLongDateTimeFormat(item.activated_at * 1000)]) : null
                }
            ];

            for (const col of cols) {
                const meta = this.metadata[col.field];

                let label = col.field;
                if (isObject(meta) && meta.hasOwnProperty('label')) {
                    label = meta.label;
                }

                col.label = col.label || label;
            }

            return cols;
        },

        /**
         * Renders the cell with the given field and item
         *
         * @param {object} cellConfig the configuration for the cell
         * @param {object} item the FP typed item
         *
         * @returns {string} a human readable version of the given field
         */
        render(cellConfig, item) {
            if (cellConfig.format) {
                return cellConfig.format(item);
            }
            return item[cellConfig.field] || '×';
        },

        /**
         * Renders the cell tooltip if available
         *
         * @param {object} cellConfig the configuration for the cell
         * @param {object} item the FP typed item
         *
         * @returns {string} a tooltip text
         */
        renderTooltip(cellConfig, item) {
            if (isFunction(cellConfig.tip)) {
                return cellConfig.tip(item, this.$i18n);
            }
        },

        /**
         * Compiles a link in the context of the given cell
         *
         * @param {string} link the link for the cell
         * @param {object} item the FP typed item
         *
         * @returns {string} a compiled version of the link for this cell
         */
        cellLink(link, item) {
            return this.$services.documents.buildUrl(link, { eventId: this.eid, ...item });
        },

        /**
         * Items selection event handler
         *
         * @param {object[]} items the set of selected items
         */
        onSelectedItems(items) {
            this.someActiveSelected = items.some(i => i.fp_status === 'active' || i.fp_status === 'cancelled');
            this.someHiddenSelected = items.some(i => i.fp_status === 'hidden' || i.fp_status === 'cancelled');
        },

        /**
         * Sets the sortable field and reload the collection
         *
         * @param {object} cellConfig the configuration for the cell
         */
        setSortable(cellConfig) {
            const { sort, sortField } = this.$utils.tables.setSortable(cellConfig, this.sort, this.sortField);
            this.sort = sort;
            this.sortField = sortField;
            this.loadData();
        },

        /**
         * @returns {string} the HREF for the user creation page
         */
        getNewPaxHref() {
            const route = get(this.params, 'module.controllers.create.route');
            return route.replace(':eventId', this.eid);
        },

        /**
         * @returns {string} the HREF for the users list import
         */
        getExcelImportHref() {
            return this.$utils.url.getImportExportHref('participants/import', this.eid, 'event');
        },

        /**
         * Opens the modal for sending emails to the users
         */
        openEmailPrompt() {
            const event = new CustomEvent(this.$const.EMAIL_MODAL_EVENT, { detail: {
                users: this.$refs['users'].selectedItems,
                event: this.params.event,
            } });
            window.dispatchEvent(event);
        },

        /**
         * Marks the user(s) status as requested
         *
         * @param {'active'|'hidden'|'cancelled'} fpStatus the status to mark the user(s) with
         */
        async markPaxAs(fpStatus) {
            try {
                if (this.processing) {
                    return;
                }

                this.processing = true;

                const changes = { fp_status: fpStatus };
                const options = { fp_status: 'replace-all' };
                const selectedPaxIds = this.$refs['users'].selectedItemsIds;

                const { jobId } = await this.$services.documents.bulkEditDocs(this.eid, 'person', changes, options, selectedPaxIds);
                await this.$services.jobs.waitFor({ jobId, resultPath: this.$const.QUEUE_JOB_STATUS_PATH.replace('{{jobId}}', jobId.toString()) });
                await this.loadData();
                this.onSelectedItems(this.$refs['users'].selectedItems);

                this.$notify({
                    group: 'flashes',
                    text: this.$i18n.tc('listings.messages.users_status_changed', [selectedPaxIds.length]).toString(),
                    type: 'success',
                    duration: 5000
                });

            } catch (error) {
                console.error('[UsersList] Could not mark the user', error.message);

                this.$notify({
                    group: 'flashes',
                    title: this.$i18n.t('general.error_occurred').toString(),
                    text: this.$i18n.t('general.try_later').toString(),
                    type: 'error',
                    duration: 5000
                });

            } finally {
                this.processing = false;
            }
        },

        /**
         * Shows the prompt for user(s) deletion
         */
        deletePax() {
            this.$modal.show('dialog', {
                text: this.$t('listings.messages.user_deletion_prompt'),
                buttons: [
                    {
                        title: this.$t('general.dismiss'),
                        default: true,
                        class: 'btn'
                    },
                    {
                        title: this.$t('listings.actions.deactivate'),
                        handler: () => {
                            this.markPaxAs('cancelled');
                            this.$modal.hide('dialog');
                        },
                        class: 'btn btn-danger'
                    }
                ]
            });
        },

        /**
         * Opens the export users list modal
         */
        openExportModal() {
            this.$modal.show(ListExportModal, {
                title: 'Participants',
                fpType: 'person',
                exporter: 'lib/person/fp-type-exporter',
                event: this.params.event
            }, {
                classes: 'small',
                height: 'auto',
                clickToClose: false
            });
        },

        /**
         * Opens the bulk editor
         */
        bulkEdit() {
            const selectedPax = this.$refs['users'].selectedItems;
            this.$modal.show(BulkEditor, {
                event: this.params.event,
                title: this.$t('listings.editing.title'),
                submitLabel: 'listings.actions.edit_users',
                items: selectedPax,
                fpType: 'person',
                introText: 'listings.messages.editing_users',
                onSuccess: async () => await this.loadData()
            }, {
                classes: 'small',
                height: 'auto'
            });
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"users-list fp-typed-list"},[_c('smart-table',{ref:"users",attrs:{"track-by":"_id","hide-pagination":_vm.searchTerm.length > 0,"items-per-page":_vm.$services.pax.MAX_PAX_PER_PAGE,"items":_vm.rows,"selectable":true,"total-items":_vm.total || _vm.rows.length},on:{"next":_vm.loadData,"previous":_vm.loadData,"select-page":_vm.loadData,"selected-items":_vm.onSelectedItems},scopedSlots:_vm._u([{key:"filters",fn:function(ref){return [_c('search-bar',{attrs:{"show-no-result":false},model:{value:(_vm.searchTerm),callback:function ($$v) {_vm.searchTerm=$$v},expression:"searchTerm"}})]}},{key:"actions",fn:function(ref){return [_c('a',{staticClass:"btn btn-primary",attrs:{"href":_vm.getNewPaxHref()}},[_vm._v(_vm._s(_vm.$t('listings.actions.new_user')))]),_c('a',{staticClass:"btn",on:{"click":function($event){return _vm.openExportModal()}}},[_vm._v(_vm._s(_vm.$t('listings.actions.export_xls')))]),_c('a',{staticClass:"btn",attrs:{"href":_vm.getExcelImportHref()}},[_vm._v(_vm._s(_vm.$t('listings.actions.import_xls')))]),(_vm.canSendEmails)?_c('a',{staticClass:"btn",on:{"click":function($event){return _vm.openEmailPrompt()}}},[_vm._v(_vm._s(_vm.$t('users.email_modal.title')))]):_vm._e()]}},{key:"bulk-actions",fn:function(ref){return [_c('loader-button',{staticClass:"btn-danger",attrs:{"status":_vm.processing},on:{"click":function($event){return _vm.deletePax()}}},[_vm._v(_vm._s(_vm.$t('listings.actions.delete_user')))]),_c('loader-button',{attrs:{"status":_vm.processing},on:{"click":function($event){return _vm.bulkEdit()}}},[_vm._v(_vm._s(_vm.$t('listings.actions.bulk_edit')))]),_c('loader-button',{attrs:{"status":_vm.processing,"disabled":!_vm.someActiveSelected},on:{"click":function($event){return _vm.markPaxAs('hidden')}}},[_vm._v(_vm._s(_vm.$t('listings.actions.mark_user_hidden')))]),_c('loader-button',{attrs:{"status":_vm.processing,"disabled":!_vm.someHiddenSelected},on:{"click":function($event){return _vm.markPaxAs('active')}}},[_vm._v(_vm._s(_vm.$t('listings.actions.mark_user_active')))]),(_vm.canSendEmails)?_c('loader-button',{attrs:{"status":_vm.processing},on:{"click":function($event){return _vm.openEmailPrompt()}}},[_vm._v(_vm._s(_vm.$t('users.email_modal.title')))]):_vm._e()]}},{key:"headers",fn:function(ref){return _vm._l((_vm.cols),function(c){return _c('th',{class:[c.cssClass || c.field ].concat( _vm.$utils.tables.getCellSortClass(c, _vm.sort, _vm.sortField)),on:{"click":function($event){return _vm.setSortable(c)}}},[_c('span',[_vm._v(_vm._s(_vm.$te(c.label) ? _vm.$t(c.label) : c.label))]),_c('i',{staticClass:"ic-icon"})])})}},{key:"row",fn:function(ref){
var item = ref.item;
return _vm._l((_vm.cols),function(c){return _c('td',{directives:[{name:"tooltip",rawName:"v-tooltip",value:(_vm.renderTooltip(c, item)),expression:"renderTooltip(c, item)"}],class:_vm.$utils.tables.cellClasses(c, item)},[(c.link)?_c('a',{attrs:{"href":_vm.cellLink(c.link, item)}},[_vm._v(_vm._s(_vm.render(c, item)))]):_c('span',[_vm._v(_vm._s(_vm.render(c, item)))])])})}}])})],1)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "UsersList.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__